import Button from "components/Button"
import { FormatCurrency } from "components/Currency"
import MenuRadio from "components/MenuRadio"
import useEnv from "hooks/useEnv"
import { toPng } from "html-to-image"
import { type FC, useRef, useState } from "react"
import { Trans } from "react-i18next"
import type { Offer } from "../../offersList/offersListTypes"

import styles from "./PostsGecontur.module.css"

interface VariantOption {
  label: React.ReactNode
  value: string
}

const variants: Record<string, string> = {
  transparent: styles.Transparent,
  blue: styles.Blue,
  yellow: styles.Yellow,
  top: styles.Top,
  center: styles.Center,
  fb: styles.Facebook,
  insta: styles.Instagram,
  subOpaque: styles.SubOpaque,
  subFullTransparent: styles.SubFullTransparent,
  subTransparent: styles.SubTransparent,
}

interface Props {
  offer: Offer
}
const PostsGecontur: FC<Props> = ({ offer }) => {
  const domEl = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState<string>("fb")
  const [position, setPosition] = useState<string>("top")
  const [color, setColor] = useState<string>("transparent")
  const [subtitle, setSubtitle] = useState<string>("subFullTransparent")
  const [logoColor, setLogoColor] = useState<string>("logoWhite")
  const { geconturUrl } = useEnv()

  const logoBlue = `${geconturUrl}logo-blue.png`
  const logoWhite = `${geconturUrl}logo-white.png`

  const sizeOptions: VariantOption[] = [
    { label: <Trans i18nKey="offersPost.controls.size.post" />, value: "fb" },
    {
      label: <Trans i18nKey="offersPost.controls.size.story" />,
      value: "insta",
    },
  ]
  const colorOptions: VariantOption[] = [
    {
      label: <Trans i18nKey="offersPost.controls.color.transparentTitle" />,
      value: "transparent",
    },
    {
      label: <Trans i18nKey="offersPost.controls.color.blue" />,
      value: "blue",
    },
    {
      label: <Trans i18nKey="offersPost.controls.color.yellow" />,
      value: "yellow",
    },
  ]
  const positionOptions: VariantOption[] = [
    {
      label: <Trans i18nKey="offersPost.controls.position.top" />,
      value: "top",
    },
    {
      label: <Trans i18nKey="offersPost.controls.position.center" />,
      value: "center",
    },
  ]
  const subtitleOptions: VariantOption[] = [
    {
      label: <Trans i18nKey="offersPost.controls.subtitle.transparent" />,
      value: "subFullTransparent",
    },
    {
      label: <Trans i18nKey="offersPost.controls.subtitle.whiteTransparent" />,
      value: "subTransparent",
    },
    {
      label: <Trans i18nKey="offersPost.controls.subtitle.white" />,
      value: "subOpaque",
    },
  ]
  const logoOptions: VariantOption[] = [
    {
      label: <Trans i18nKey="offersPost.controls.logo.white" />,
      value: "logoWhite",
    },
    {
      label: <Trans i18nKey="offersPost.controls.logo.blue" />,
      value: "logoBlue",
    },
  ]

  const downloadImage = async (): Promise<void> => {
    if (!domEl.current) return

    try {
      const dataUrl = await toPng(domEl.current)

      // download image
      const link = document.createElement("a")
      link.download = `oferta-${offer.id}.png`
      link.href = dataUrl
      link.click()
    } catch (error) {
      console.error("Error generating image:", error)
    }
  }

  const logo = logoColor === "logoWhite" ? logoWhite : logoBlue

  const {
    AccommodationName,
    OfferTitle,
    LeadInPrice,
    PriceType,
    ImageURL,
    Nights,
  } = offer

  return (
    <main className={`px-4 ${variants[size]}`}>
      <div className="mx-auto grid justify-center px-4">
        <div className={styles.FrameWrapper}>
          <div className={styles.Frame}>
            <div ref={domEl}>
              <div className={`${styles.Root}`}>
                <img
                  className={styles.CoverImage}
                  alt={OfferTitle}
                  src={ImageURL}
                />
                <div
                  className={`${variants[color]} ${variants[position]} ${variants[subtitle]}`}
                >
                  <div className="text-center">
                    <div className={styles.Title} contentEditable="true">
                      {OfferTitle}
                    </div>
                    <div className={styles.Subtitle} contentEditable="true">
                      {Nights && (
                        <Trans
                          i18nKey="offersPost.labels.nightsAt"
                          values={{ count: Nights }}
                        />
                      )}{" "}
                      <b contentEditable="true">{AccommodationName}</b>{" "}
                      {PriceType} <FormatCurrency value={LeadInPrice} />
                    </div>
                  </div>
                </div>
                <img alt={OfferTitle} className={styles.Logo} src={logo} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-3/4 my-1 text-center flex flex-col gap-2 p-2 mx-auto">
        <MenuRadio selected={size} options={sizeOptions} onClick={setSize} />
        <MenuRadio
          selected={position}
          options={positionOptions}
          onClick={setPosition}
        />
        <MenuRadio selected={color} options={colorOptions} onClick={setColor} />
        <MenuRadio
          selected={subtitle}
          options={subtitleOptions}
          onClick={setSubtitle}
        />
        <MenuRadio
          selected={logoColor}
          options={logoOptions}
          onClick={setLogoColor}
        />
      </div>

      <div className="my-4 flex justify-center">
        <Button primary medium onClick={downloadImage}>
          <Trans i18nKey="offersPost.actions.downloadImage" />
        </Button>
      </div>
    </main>
  )
}

export default PostsGecontur
